/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  CircularProgress,
} from '@mui/material'
import React, { useState } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import { authFetch } from '~/services/fetch'
import { getKeywordSearchLogs as getUserActionsReportSearchLogs } from '../services'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Tooltip from '@mui/material/Tooltip'
import AuditUserActionsReportFilters from './AuditUserActionsReportFilters'
import { buildReportTypeOptions } from './reportTypeOptions'
import AuditLogDetailsModal from '../../AuditLogDetailsModal'
import getModalFields from './getModalFields'
import type {
  UserActionsReportSearchLog,
  UserActionsReportSearchLogRow,
  SearchLogResponse,
} from '../interfaces'

const extractDatesFromContent = (content: string) => {
  try {
    const parsedContent = JSON.parse(content)
    return {
      start_date: parsedContent.start_date || null,
      finish_date: parsedContent.finish_date || null,
    }
  } catch {
    return {
      start_date: null,
      finish_date: null,
    }
  }
}

const validatePeriod = (
  startDate: string | null,
  finishDate: string | null,
  translateFn: (s: string) => string
) => {
  if (!startDate || !finishDate) {
    notify({
      type: 'error',
      message: translateFn('Start date and end date must be provided'),
    })
    return false
  }
  return true
}

const buildPayload = (params: {
  readonly userId?: number | null
  readonly stationId?: number | null
  readonly route?: string | null
  readonly startDate: string
  readonly finishDate: string
  readonly page: number
  readonly pageSize: number
  readonly orders: readonly { readonly field: string; readonly sort: string }[]
}) => {
  return {
    user_id: params.userId || undefined,
    station_id: params.stationId,
    route: params.route || undefined,
    start_date: params.startDate,
    finish_date: params.finishDate,
    page: params.page,
    limit: params.pageSize,
    orders: params.orders,
  }
}

const mapElementToRow = (
  element: UserActionsReportSearchLog,
  index: number,
  page: number,
  pageSize: number,
  getReportNameFromRoute: (route?: string) => string,
  translateFn: (s: string) => string
): UserActionsReportSearchLogRow => {
  const generatedId = element.id ?? page * pageSize + index + 1
  const reportDisplay = element.route
    ? getReportNameFromRoute(element.route)
    : translateFn('Unknown Report')
  const actionDisplay = element.type || translateFn('Unknown Action')

  const { start_date: contentStartDate, finish_date: contentFinishDate } =
    element.content
      ? extractDatesFromContent(element.content)
      : { start_date: null, finish_date: null }

  const contentPeriod =
    contentStartDate || contentFinishDate
      ? `${contentStartDate ? dayjs(contentStartDate).format('DD/MM/YYYY HH:mm') : ''}${
          contentStartDate && contentFinishDate ? ' - ' : ''
        }${contentFinishDate ? dayjs(contentFinishDate).format('DD/MM/YYYY HH:mm') : ''}`
      : null

  return {
    id: generatedId,
    ...element,
    'user:representation_name': element?.user_name || '',
    report_display: reportDisplay,
    action_display: actionDisplay,
    content_start_date: contentStartDate ?? undefined,
    content_finish_date: contentFinishDate ?? undefined,
    content_period: contentPeriod ?? undefined,
  }
}

const mapResponseToRows = (
  data: readonly UserActionsReportSearchLog[],
  page: number,
  pageSize: number,
  getReportNameFromRoute: (route?: string) => string,
  translateFn: (s: string) => string
): readonly UserActionsReportSearchLogRow[] =>
  data.map((element, index) =>
    mapElementToRow(element, index, page, pageSize, getReportNameFromRoute, translateFn)
  )

export default function ListAllAuditUserActionsReport() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const stationId = useHookstate<number | null>(null)
  const reportType = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const resetValues = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const limit = 15
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'
  const expandUserActions = useHookstate<boolean>(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] =
    useState<UserActionsReportSearchLogRow | null>(null)
  const [loadingRowId, setLoadingRowId] = useState<number | null>(null)
  const [openTooltips, setOpenTooltips] = useState<Record<number, boolean>>({})

  const handleView = async (row: UserActionsReportSearchLogRow) => {
    setLoadingRowId(row.id ?? null)
    try {
      if (row.id == null) {
        notify({
          type: 'error',
          message: translate('Invalid log id'),
        })
        return
      }

      const response = await authFetch({
        url: `/logs/details/${Number(row.id)}`,
        method: 'GET',
      })

      if (!response || response.status !== 200) {
        throw new Error('Erro ao buscar detalhes do log')
      }

      setSelectedRow(response.data)
      setOpenModal(true)
    } catch (error) {
      notify({
        type: 'error',
        message: translate('Erro ao carregar detalhes do log'),
      })
    } finally {
      setLoadingRowId(null)
    }
  }

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setOpenModal(false)
    }
  }

  const reportTypeOptions = buildReportTypeOptions(translate)

  const getReportNameFromRoute = (route?: string): string => {
    if (!route) return translate('Unknown Report')
    const found = reportTypeOptions.find((opt) => opt.value === route)
    return found ? found.label : route
  }

  const columns: readonly GridColDef[] = [
    { field: 'id' },
    {
      field: 'user:representation_name',
      headerName: translate('User'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'station',
      headerName: translate('Station'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'report_display',
      headerName: translate('Report'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'action_display',
      headerName: translate('action'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'content_period',
      headerName: translate('Period'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (value, row) => {
        const start = row.content_start_date
        const finish = row.content_finish_date
        if (!start && !finish) return null
        if (start && finish)
          return `${dayjs(start).format('DD/MM/YYYY HH:mm')} - ${dayjs(
            finish
          ).format('DD/MM/YYYY HH:mm')}`
        return start ? dayjs(start).format('DD/MM/YYYY HH:mm') : dayjs(finish).format('DD/MM/YYYY HH:mm')
      },
    },
    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) =>
        dayjs(value as string).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip
            title={translate('Visualizar')}
            open={openTooltips[String((params.row as UserActionsReportSearchLogRow).id)] ?? false}
            onOpen={() =>
              setOpenTooltips(prev => ({
                ...prev,
                [String((params.row as UserActionsReportSearchLogRow).id)]: true,
              }))
            }
            onClose={() =>
              setOpenTooltips(prev => ({
                ...prev,
                [String((params.row as UserActionsReportSearchLogRow).id)]: false,
              }))
            }
          >
            <IconButton
              onClick={() => {
                setOpenTooltips(prev => ({
                  ...prev,
                  [String((params.row as UserActionsReportSearchLogRow).id)]: false,
                })) // Força o fechamento do tooltip ao clicar
                handleView(params.row as UserActionsReportSearchLogRow)
              }}
              size="small"
              aria-label={translate('Visualizar')}
              disabled={loadingRowId === (params.row as UserActionsReportSearchLogRow).id}
            >
              {loadingRowId === (params.row as UserActionsReportSearchLogRow).id ? (
                <CircularProgress size={20} />
              ) : (
                <VisibilityIcon />
              )}
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    stationId.set(null)
    reportType.set(null)
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    expandUserActions.set(false)
  }

  const submit = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ) => {
    if (!validatePeriod(startDate, finishDate, translate)) {
      return false
    }

    loadingButton.set(true)

    const page = model.page
    const pageSize = model.pageSize

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort || 'desc',
    }))

    const payload = buildPayload({
      userId: userId.get(),
      stationId: stationId.get(),
      route: reportType.get(),
      startDate: startDate as string,
      finishDate: finishDate as string,
      page,
      pageSize,
      orders: orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
    })

    const searchLogData: SearchLogResponse =
      await getUserActionsReportSearchLogs(payload).finally(() => {
        loadingButton.set(false)
        expandUserActions.set(true)
      })

    const formattedRows = mapResponseToRows(
      searchLogData.data,
      page,
      pageSize,
      getReportNameFromRoute,
      translate
    )

    setRows(formattedRows)
    setRowCount(searchLogData.rowsCount)
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    submit(firstPageModel, sortModel)
  }

  const modalFields = React.useMemo(() => getModalFields(selectedRow, translate), [selectedRow, translate])

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditUserActionsReportFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          focusChange={focusChange}
          resetValues={resetValues}
          userId={userId}
          initialPeriod={initialPeriod}
          stationId={stationId}
          reportType={reportType}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => searchFromFirstPage()}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() => expandUserActions.set(!expandUserActions.get())}
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandUserActions.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandUserActions.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              submit(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
              }
              setPaginationModel(firstPageModel)
              submit(firstPageModel, model)
            }}
            columnVisibilityModel={{
              id: false,
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <AuditLogDetailsModal
        open={openModal}
        onClose={clickOutModalClose}
        selectedRow={selectedRow}
        fieldsToDisplay={modalFields}
      />
    </>
  )
}
